import classNames from 'classnames'
import { FC, useState } from 'react'
import { DeepDive } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './deepDive.module.scss'
import { MeterBar } from '../shared/charts/MeterBar'
import { BAND_TONES, Pill, PillTone } from '../shared/Pill'
import { BAND_LABELS, BAND_LEGEND, formatNumber } from '../shared/utils'

// ---------- topic clusters ----------

export const TopicClusters: FC<{ clusters: DeepDive['topicClusters'] }> = ({
  clusters,
}) => (
  <section className={styles.card}>
    <div className={styles.cardHeader}>
      <div>
        <h2 className={styles.cardTitle}>Topic Clusters Visibility</h2>
        <p className={styles.cardSubtitle}>
          AI visibility across the key topics and themes shaping your brand
        </p>
      </div>
      <div className={styles.legend}>
        {BAND_LEGEND.map((item) => (
          <span className={styles.legendItem} key={item.band}>
            <span className={classNames(styles.legendDot, styles[item.band])} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
    <div className={classNames(styles.topicGrid, styles.topicHead)}>
      <span>Topic</span>
      <span>Visibility</span>
      <span>Score</span>
      <span className={styles.alignRight}>Status</span>
    </div>
    {clusters.length === 0 && (
      <p className={styles.emptyText}>No topics were weighed in this run.</p>
    )}
    {clusters.map((cluster) => (
      <div
        className={classNames(styles.topicGrid, styles.topicRow)}
        key={cluster.topic}
      >
        <span className={styles.topicName}>{cluster.topic}</span>
        <MeterBar band={cluster.band} ticks={[40, 60]} value={cluster.score} />
        <span className={styles.topicScore}>{formatNumber(cluster.score)}</span>
        <span className={styles.alignRight}>
          <Pill dot tone={BAND_TONES[cluster.band]}>
            {BAND_LABELS[cluster.band]}
          </Pill>
        </span>
      </div>
    ))}
  </section>
)

// ---------- attribute ownership ----------

type SortKey = 'consensus' | 'leader'

const CONSENSUS_ORDER = { high: 0, low: 2, medium: 1 }
const CONSENSUS_TONES: Record<'high' | 'low' | 'medium', PillTone> = {
  high: 'green',
  low: 'red',
  medium: 'amber',
}
const CONSENSUS_LABELS = { high: 'High', low: 'Low', medium: 'Medium' }

const SortIcon: FC<{ direction?: 'asc' | 'desc' }> = ({ direction }) => (
  <svg fill="none" height="12" viewBox="0 0 12 12" width="12">
    <path
      d="M3.5 4.5L6 2l2.5 2.5"
      stroke={direction === 'asc' ? '#8080ff' : 'currentColor'}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 7.5L6 10l2.5-2.5"
      stroke={direction === 'desc' ? '#8080ff' : 'currentColor'}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const AttributeOwnership: FC<{
  attributes: DeepDive['attributeOwnership']
}> = ({ attributes }) => {
  const [sort, setSort] = useState<{
    direction: 'asc' | 'desc'
    key: SortKey
  } | null>(null)

  const toggleSort = (key: SortKey) =>
    setSort((previous) =>
      previous?.key === key
        ? { direction: previous.direction === 'asc' ? 'desc' : 'asc', key }
        : { direction: 'asc', key }
    )

  const rows = [...attributes]
  if (sort) {
    const sign = sort.direction === 'asc' ? 1 : -1
    rows.sort((a, b) =>
      sort.key === 'leader'
        ? sign * a.leader.localeCompare(b.leader)
        : sign * (CONSENSUS_ORDER[a.consensus] - CONSENSUS_ORDER[b.consensus])
    )
  }

  return (
    <section className={styles.card}>
      <div>
        <h2 className={styles.cardTitle}>AI Attribute Ownership</h2>
        <p className={styles.cardSubtitle}>
          Which brand AI most strongly associates with each attribute
        </p>
      </div>
      <div className={classNames(styles.attributeGrid, styles.attributeHead)}>
        <span>Attribute</span>
        <button
          className={styles.sortButton}
          onClick={() => toggleSort('leader')}
          type="button"
        >
          AI Leader
          <SortIcon
            direction={sort?.key === 'leader' ? sort.direction : undefined}
          />
        </button>
        <button
          className={styles.sortButton}
          onClick={() => toggleSort('consensus')}
          type="button"
        >
          Consensus
          <SortIcon
            direction={sort?.key === 'consensus' ? sort.direction : undefined}
          />
        </button>
      </div>
      {rows.length === 0 && (
        <p className={styles.emptyText}>
          No attributes were weighed in this run.
        </p>
      )}
      <div className={styles.attributeRows}>
        {rows.map((row) => (
          <div
            className={classNames(styles.attributeGrid, styles.attributeRow)}
            key={row.attribute}
          >
            <span>{row.attribute}</span>
            <span
              className={row.leaderIsTarget ? styles.accent : styles.strong}
            >
              {row.leader}
            </span>
            <span>
              <Pill outline tone={CONSENSUS_TONES[row.consensus]}>
                {CONSENSUS_LABELS[row.consensus]} {row.count}/{row.of}
              </Pill>
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

// ---------- perception clusters ----------

const PERCEPTION_BOXES: {
  className: string
  key: keyof DeepDive['perception']
  title: string
}[] = [
  {
    className: 'perceptionStrong',
    key: 'strong',
    title: 'Strong Associations',
  },
  { className: 'perceptionWeak', key: 'weak', title: 'Weak Associations' },
  {
    className: 'perceptionBad',
    key: 'disadvantages',
    title: 'Disadvantages',
  },
  {
    className: 'perceptionEmerging',
    key: 'emerging',
    title: 'Emerging Narratives',
  },
]

export const PerceptionClusters: FC<{
  brandName: string
  perception: DeepDive['perception']
}> = ({ brandName, perception }) => (
  <section className={styles.card}>
    <div>
      <h2 className={styles.cardTitle}>AI Brand Perception Clusters</h2>
      <p className={styles.cardSubtitle}>
        How AI models associate {brandName} with key brand attributes and
        perceptions
      </p>
    </div>
    <div className={styles.perceptionGrid}>
      {PERCEPTION_BOXES.map((box) => (
        <div className={styles.perceptionBox} key={box.key}>
          <span
            className={classNames(
              styles.perceptionTitle,
              styles[box.className]
            )}
          >
            {box.title}
          </span>
          {perception[box.key].length === 0 ? (
            <span className={styles.muted}>None identified</span>
          ) : (
            <ul className={styles.perceptionList}>
              {perception[box.key].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  </section>
)
